import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
// One role hierarchy, shared (src/data/roleLevels.js). This file used to
// carry its own copy, which is how four roles went missing from it.
import { roleLevel, gateLevel } from '../../data/roleLevels';
// One predicate for "may this user open this surface" — the left navigation
// asks it too (data/routeGates.js), so a link can never appear in the rail and
// then refuse when opened, nor be reachable by URL and missing from the rail.
import { canAccess } from '../../data/routeGates';

/**
 * RoleGuard — Route-level RBAC enforcement.
 *
 * Wraps a route element and checks the current user's role/permissions
 * before rendering children. Redirects unauthorized users to /overview
 * with a clear message.
 *
 * Props:
 *   requiredPermission  — single permission string (e.g., "workflow.create")
 *   anyPermission       — array of permissions; user needs at least one
 *   minRole             — minimum role name (e.g., "Developer", "Tenant Admin")
 *   exactRole           — require this EXACT role name (e.g., "Platform Admin").
 *                         Platform Admin (6) sits above Super Admin (5), so
 *                         minRole="Platform Admin" already excludes SA. exactRole
 *                         is the stricter, intent-revealing form: it pins the gate
 *                         to one role and stays correct even if a higher tier is
 *                         ever added above PA. It's the route-level analogue of the
 *                         backend's isPlatformAdmin() (platform-wide) vs
 *                         isSuperAdmin() (customer-scoped) distinction — used for
 *                         platform-wide surfaces like /flags. Never club PA + SA.
 *   children            — the protected route content
 *
 * Usage:
 *   <Route path="canvas" element={<RoleGuard requiredPermission="workflow.read"><CanvasPage /></RoleGuard>} />
 *   <Route path="team" element={<RoleGuard minRole="Tenant Admin"><TeamPage /></RoleGuard>} />
 *   <Route path="flags" element={<RoleGuard exactRole="Platform Admin"><FlagsPage /></RoleGuard>} />
 */


export default function RoleGuard({ requiredPermission, anyPermission, minRole, exactRole, children }) {
  const { hasPermission, session } = useAuth();
  const currentRole = session?.role || 'Viewer';
  const currentLevel = roleLevel(currentRole);

  // G422 — Track each gate individually so the Access Denied message can
  // tell the user the SPECIFIC reason they were blocked, not all of them.
  // Pre-G422 the page emitted both "requires X role" AND "missing Y permission"
  // even when the user actually had the permission and only failed the
  // role check (e.g. a Viewer hitting /canvas, which is gated on both
  // requiredPermission="workflow.read" AND minRole="Developer" — Viewer
  // has workflow.read but is below Developer, so the message wrongly
  // claimed workflow.read was missing).
  const permFailed = !!requiredPermission && !hasPermission(requiredPermission);
  const anyPermFailed = Array.isArray(anyPermission) && anyPermission.length > 0
                       && !anyPermission.some(p => hasPermission(p));
  const roleFailed = !!minRole && currentLevel < gateLevel(minRole);
  // Exact-role gate — strict string equality, NOT a level comparison. Pins the
  // route to exactly one role (e.g. "Platform Admin"). With PA=6 > SA=5 a
  // minRole gate would also exclude Super Admin, but exactRole states the intent
  // explicitly and stays correct if a tier is ever added above PA.
  const exactRoleFailed = !!exactRole && currentRole !== exactRole;

  /* The decision comes from the shared predicate. The four flags above survive
     ONLY to word the refusal — G422 wants the specific reason, not all of them
     — and must never be the thing that decides, or this guard and the rail
     would be two implementations of one rule again. */
  const allowed = canAccess(
    { requiredPermission, anyPermission, minRole, exactRole },
    { hasPermission, role: currentRole },
  );

  if (!allowed) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        height: '60vh', textAlign: 'center', fontFamily: 'system-ui, sans-serif',
      }}>
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12,
          padding: '32px 48px', maxWidth: 480,
        }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>🔒</div>
          <h2 style={{ color: '#991b1b', fontSize: 18, margin: '0 0 8px 0' }}>Access Denied</h2>
          <p style={{ color: '#7f1d1d', fontSize: 13, margin: '0 0 16px 0', lineHeight: 1.5 }}>
            Your role ({currentRole}) does not have permission to access this page.
            {roleFailed && <> This page requires at least <strong>{minRole}</strong> access.</>}
            {exactRoleFailed && <> This page is restricted to <strong>{exactRole}</strong> only.</>}
            {permFailed && <> Missing permission: <strong>{requiredPermission}</strong>.</>}
            {anyPermFailed && (
              <> Missing one of: <strong>{anyPermission.join(', ')}</strong>.</>
            )}
          </p>
          <a
            href="/overview"
            style={{
              display: 'inline-block', padding: '8px 20px', background: '#dc2626', color: '#fff',
              borderRadius: 6, fontSize: 13, fontWeight: 600, textDecoration: 'none',
            }}
          >
            Return to Overview
          </a>
        </div>
      </div>
    );
  }

  return children;
}
